import { Component, OnInit } from '@angular/core';
import { PortfolioService } from '../../../../core/services/api/portfolio.service';
import { finalize } from 'rxjs/operators';
import { ErrorService } from '../../../../core/services/error.service';
import { PortfolioListModel } from '../../../../core/models/portfolio/portfolio-list.model';
import { ShareMarketStatus } from '../../../../core/enum/mysec/share-market-status.enum';
import { MonthlyStatementResponse } from '../../../../core/models/portfolio/monthly-statement';
import { MatDialog } from '@angular/material/dialog';
import { TaxExemptionPreviewComponent } from './tax-exemption-preview/tax-exemption-preview.component';
import moment from 'moment';

@Component({
    selector: 'app-statements',
    templateUrl: './statements.component.html',
    styleUrls: ['./statements.component.scss'],
    standalone: false
})
export class StatementsComponent implements OnInit {
  isLoading = false;
  params = {
    currentPage: 1,
    take: 10,
    isDigitalCert: true,
    includeMonthlyStatements: true,
  };
  total: number = 0;
  monthlyStatements: { year: number; monthlyStatements: MonthlyStatementResponse[] }[] = [];
  taxCertificates: any[] = [];
  displayedTaxCertificates: any[] = [];
  taxParams = {
    currentPage: 1,
    take: 10,
  };
  taxTotal: number = 0;

  portfolios: PortfolioListModel[] = [];
  ShareMarketStatus = ShareMarketStatus;

  constructor(
    private readonly portfolioService: PortfolioService,
    private readonly errorService: ErrorService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.getDigitalCerts();
    this.getTaxExemptionCertificates();
  }

  getDigitalCerts() {
    this.isLoading = true;

    this.portfolioService
      .getPortfolio(
        this.params.currentPage,
        this.params.take,
        this.params.isDigitalCert,
        this.params.includeMonthlyStatements,
      )
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (data) => {
          this.portfolios = (data?.data ?? []).filter((p) => p.digitalCert);
          this.total = data?.total ?? 0;
          this.monthlyStatements = this.groupByYear(data?.monthlyStatements ?? []);
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  getTaxExemptionCertificates() {
    this.portfolioService.getTaxExemptionCertificates().subscribe({
      next: (items) => {
        this.taxCertificates = items ?? [];
        this.taxTotal = this.taxCertificates.length;
        this.taxParams.currentPage = 1;
        this.updateTaxDisplay();
      },
      error: (err) => {
        this.errorService.showError(err);
      },
    });
  }

  updateTaxDisplay() {
    const start = (this.taxParams.currentPage - 1) * this.taxParams.take;
    const end = start + this.taxParams.take;
    this.displayedTaxCertificates = this.taxCertificates.slice(start, end);
  }

  onTaxPageChange(page: number) {
    this.taxParams.currentPage = page;
    this.updateTaxDisplay();
  }

  showCounterLink(portfolio: PortfolioListModel) {
    return portfolio?.tickerCode && portfolio?.status !== ShareMarketStatus.Dehosting;
  }

  openUrl(url: string) {
    window.open(url);
  }

  groupByYear(
    monthlyStatements: MonthlyStatementResponse[] = [],
    descending: boolean = true,
  ): { year: number; monthlyStatements: MonthlyStatementResponse[] }[] {
    const grouped: Record<number, MonthlyStatementResponse[]> = monthlyStatements.reduce(
      (acc, item) => {
        const year = moment(item.generationDate).add(-1, 'months').year();
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(item);
        return acc;
      },
      {} as Record<number, MonthlyStatementResponse[]>,
    );

    return Object.entries(grouped)
      .map(([year, items]) => ({
        year: parseInt(year, 10),
        monthlyStatements: items.sort((a, b) => {
          const dateA = new Date(a.generationDate).getTime();
          const dateB = new Date(b.generationDate).getTime();
          return descending ? dateB - dateA : dateA - dateB;
        }),
      }))
      .sort((a, b) => (descending ? b.year - a.year : a.year - b.year));
  }
  getTransactionMonth(date: string) {
    return moment(date).add(-1, 'months').format('MMMM yyyy');
  }

  openReviewPopUp(cert: any) {
    const ref = this.dialog.open(TaxExemptionPreviewComponent, {
      width: 'auto',
      height: 'auto',
      panelClass: 'auto-size-dialog',
      autoFocus: false,
      data: cert,
    });
    ref.afterClosed().subscribe(() => {
      this.getTaxExemptionCertificates();
    });
  }

  downloadTaxCert(cert: any) {
    const id = cert?.id;
    if (id) {
      const url = this.portfolioService.getTaxExemptionCertificatePdfUrl(id);
      window.open(url);
    }
  }
}
